/**
 * MemberBalanceList - 成员余额列表组件
 * 
 * 显示公账模式下各成员的支付情况和余额
 * 
 * @test Git Hook validation - clean shared code ✅
 */
import React from 'react';
import { View, Text, StyleSheet, ViewStyle, Platform } from 'react-native';
import Avatar from '../common/Avatar';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

export interface MemberBalance {
  /**
   * 成员用户ID
   */
  userId: string;
  /**
   * 显示名称
   */
  displayName: string;
  /**
   * 头像URL
   */
  avatarUrl: string | null;
  /**
   * 已支付金额
   */
  spent: number;
  /**
   * 应支付金额
   */
  shouldPay: number;
  /**
   * 余额（正数=多付，负数=欠款）
   */
  balance: number;
  /**
   * 份额（可选，份额模式下显示）
   */
  share?: number | null;
}

export interface MemberBalanceListProps {
  /**
   * 成员余额数据
   */
  balances: MemberBalance[];
  /**
   * 货币代码
   */
  currency?: string;
  /**
   * 是否显示份额信息
   */
  showShareInfo?: boolean;
  /**
   * 格式化货币显示
   */
  formatCurrency: (amount: number, currency?: string) => string;
  /**
   * 翻译函数
   */
  t?: (key: string) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'memberBalance.title': '成员余额',
    'memberBalance.paid': '已付',
    'memberBalance.shouldPay': '应付',
    'memberBalance.overpaid': '多付',
    'memberBalance.owes': '欠款',
    'memberBalance.share': '份额',
  };
  return translations[key] || key;
};

/**
 * MemberBalanceList 组件
 */
const MemberBalanceList: React.FC<MemberBalanceListProps> = ({
  balances,
  currency,
  showShareInfo = false,
  formatCurrency,
  t = defaultT,
  style,
}) => {
  if (balances.length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, style]}>
      <View style={styles.headerRow}>
        <View style={styles.headerIcon}>
          <Icon name="people" size={18} color={MODULE_COLORS.travel} />
        </View>
        <Text style={styles.title}>{t('memberBalance.title')}</Text>
      </View>
      
      {balances.map((member) => {
        const rawProgress = member.shouldPay > 0 ? (member.spent / member.shouldPay) * 100 : 0;
        const clampedProgress = Math.max(0, Math.min(rawProgress, 100));

        return (
          <View key={member.userId} style={styles.memberItem}>
            {/* 头部：头像、名字在左，金额信息在右 */}
            <View style={styles.memberHeader}>
              <View style={styles.memberInfo}>
                <Avatar
                  avatarUrl={member.avatarUrl}
                  displayName={member.displayName}
                  size="small"
                  style={styles.avatar}
                />
                <View style={styles.memberDetails}>
                  <Text style={styles.memberName}>{member.displayName}</Text>
                  {showShareInfo && member.share != null && (
                    <Text style={styles.memberShare}>
                      {t('memberBalance.share')}: {Number(member.share).toFixed(1)}
                    </Text>
                  )}
                </View>
              </View>

              {/* 金额信息放在右侧 */}
              <View style={styles.memberAmounts}>
                <Text style={styles.amountPaid}>
                  {t('memberBalance.paid')}: {formatCurrency(member.spent, currency)}
                </Text>
                <Text style={styles.amountShouldPay}>
                  {t('memberBalance.shouldPay')}: {formatCurrency(member.shouldPay, currency)}
                </Text>
                <Text
                  style={[
                    styles.amountBalance,
                    member.balance > 0 ? styles.amountBalancePositive : styles.amountBalanceNegative,
                  ]}
                >
                  {member.balance > 0
                    ? `${t('memberBalance.overpaid')}: ${formatCurrency(member.balance, currency)}`
                    : `${t('memberBalance.owes')}: ${formatCurrency(Math.abs(member.balance), currency)}`}
                </Text>
              </View>
            </View>

            {/* 进度条显示支付比例 */}
            <View style={styles.paymentProgress}>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${clampedProgress}%`,
                      backgroundColor: member.balance >= 0 ? '#10b981' : '#f59e0b',
                    },
                  ]}
                />
              </View>
              <Text style={styles.progressText}>
                {member.shouldPay > 0 ? `${Math.max(0, rawProgress).toFixed(1)}%` : '0%'}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerIcon: {
    marginRight: 8,
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  memberHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  memberInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    marginRight: 12,
  },
  memberDetails: {
    flex: 1,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  memberShare: {
    fontSize: 13,
    color: '#6b7280',
  },
  memberAmounts: {
    alignItems: 'flex-end',
  },
  amountPaid: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 4,
    textAlign: 'right',
  },
  amountShouldPay: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 4,
    textAlign: 'right',
  },
  amountBalance: {
    fontSize: 14,
    fontWeight: '600',
  },
  amountBalancePositive: {
    color: '#10b981',
  },
  amountBalanceNegative: {
    color: '#ef4444',
  },
  paymentProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  progressBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
    minWidth: 45,
    textAlign: 'right',
  },
});

export default MemberBalanceList;
